import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, qk, type Me, type Scope, type TenantSummary } from '@viewstream/api-client';
import { api, unauthorized } from './api';

export interface AuthState {
  me: Me | null;
  loading: boolean;
  error: ApiError | null;
  scopes: Scope[];
  login: (email: string, password: string) => Promise<void>;
  /** Admin console only: exchange the Cloudflare Access identity for a session (POST /v1/auth/cf-access). */
  ssoLogin: () => Promise<void>;
  logout: () => Promise<void>;
  switchTenant: (slug: string) => Promise<TenantSummary | null>;
  /** last successful password entry (login or re-auth), for the 5-minute freshness rule */
  lastAuthAt: number | null;
  markAuthenticated: () => void;
}

const Ctx = React.createContext<AuthState | null>(null);
const FRESHNESS_MS = 5 * 60_000;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const qc = useQueryClient();
  const [lastAuthAt, setLastAuthAt] = React.useState<number | null>(null);
  const meQ = useQuery({
    queryKey: qk.me(),
    queryFn: async () => (await api.GET('/v1/me')).data ?? null,
    retry: false,
    staleTime: 60_000,
    // Admin: a role granted or revoked meanwhile must show up without a reload (admin-p6) — focus, every 60 s, and
    // right after a call succeeds following an earlier 403 (below).
    ...(__APP__ === 'admin' ? { refetchOnWindowFocus: true, refetchInterval: 60_000 } : {}),
  });

  React.useEffect(() => {
    if (__APP__ !== 'admin') return;
    let saw403 = false;
    const is403 = (e: unknown) => e instanceof ApiError && e.status === 403;
    const onEvent = (failed: boolean, ok: boolean, key: readonly unknown[] | null) => {
      if (failed) saw403 = true;
      else if (ok && saw403 && key?.[0] !== qk.me()[0]) { saw403 = false; void qc.invalidateQueries({ queryKey: qk.me() }); }
    };
    const u1 = qc.getQueryCache().subscribe((ev) => {
      if (ev.type !== 'updated') return;
      const st = ev.query.state;
      onEvent(ev.action.type === 'error' && is403(st.error), ev.action.type === 'success', ev.query.queryKey);
    });
    const u2 = qc.getMutationCache().subscribe((ev) => {
      if (ev.type !== 'updated') return;
      const st = ev.mutation.state;
      onEvent(st.status === 'error' && is403(st.error), st.status === 'success', null);
    });
    return () => { u1(); u2(); };
  }, [qc]);

  React.useEffect(() => {
    const onUnauth = () => { qc.setQueryData(qk.me(), null); };
    unauthorized.addEventListener('unauthorized', onUnauth);
    return () => unauthorized.removeEventListener('unauthorized', onUnauth);
  }, [qc]);

  const login = React.useCallback(async (email: string, password: string) => {
    await api.POST('/v1/auth/login', { body: { email, password } });
    setLastAuthAt(Date.now());
    await qc.invalidateQueries({ queryKey: qk.me() });
    await qc.invalidateQueries({ queryKey: qk.tenants() });
  }, [qc]);

  const ssoLogin = React.useCallback(async () => {
    // Cf-Access-Jwt-Assertion is added by Cloudflare on admin.viewstream.co.il, never by the page.
    await api.POST('/v1/auth/cf-access', {} as never);
    setLastAuthAt(Date.now());
    await qc.invalidateQueries({ queryKey: qk.me() });
    await qc.invalidateQueries({ queryKey: qk.tenants() });
  }, [qc]);

  const logout = React.useCallback(async () => {
    try { await api.POST('/v1/auth/logout'); } finally {
      qc.clear();
      setLastAuthAt(null);
      // Full page load: an in-app navigate raced the cleared cache (the login screen still saw the old /v1/me and
      // bounced back into the tenant). On admin. also end the Cloudflare Access session, or SSO signs straight back in.
      window.location.assign(window.location.hostname.startsWith('admin.') ? '/cdn-cgi/access/logout' : '/login');
    }
  }, [qc]);

  const switchTenant = React.useCallback(async (slug: string) => {
    const r = await api.POST('/v1/me/tenant', { body: { slug } });
    const current = r.data?.current_tenant ?? null;
    qc.setQueryData<Me | null>(qk.me(), (old) => (old ? { ...old, current_tenant: current, scopes: r.data?.scopes ?? old.scopes } : old));
    return current;
  }, [qc]);

  const value = React.useMemo<AuthState>(() => ({
    me: meQ.data ?? null,
    loading: meQ.isPending,
    error: meQ.error instanceof ApiError && meQ.error.status !== 401 ? meQ.error : null,
    scopes: meQ.data?.scopes ?? [],
    login, ssoLogin, logout, switchTenant, lastAuthAt,
    markAuthenticated: () => setLastAuthAt(Date.now()),
  }), [meQ.data, meQ.isPending, meQ.error, login, ssoLogin, logout, switchTenant, lastAuthAt]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const c = React.useContext(Ctx);
  if (!c) throw new Error('useAuth outside AuthProvider');
  return c;
}

/** Sensitive actions need a password entered within the last 5 minutes (addendum A1). Returns whether re-auth is due. */
export function useReauth(): { fresh: boolean; reauth: (password: string) => Promise<void> } {
  const { me, lastAuthAt, markAuthenticated } = useAuth();
  const fresh = lastAuthAt !== null && Date.now() - lastAuthAt < FRESHNESS_MS;
  const reauth = React.useCallback(async (password: string) => {
    const email = me?.user?.email;
    if (!email) throw new Error('no session');
    await api.POST('/v1/auth/login', { body: { email, password } }); // re-login refreshes the session; the server keeps it
    markAuthenticated();
  }, [me, markAuthenticated]);
  return { fresh, reauth };
}

/** Inactivity warning 5 min before the 8 h idle limit (A1). Activity = any API call or pointer/keyboard event. */
export function useInactivityWarning(idleMs = 8 * 3600_000, warnBeforeMs = 5 * 60_000): boolean {
  const [warn, setWarn] = React.useState(false);
  React.useEffect(() => {
    let t: number | undefined;
    const arm = () => {
      setWarn(false);
      if (t) window.clearTimeout(t);
      t = window.setTimeout(() => setWarn(true), idleMs - warnBeforeMs);
    };
    arm();
    const evs: (keyof DocumentEventMap)[] = ['pointerdown', 'keydown', 'visibilitychange'];
    evs.forEach((e) => document.addEventListener(e, arm, { passive: true }));
    return () => { evs.forEach((e) => document.removeEventListener(e, arm)); if (t) window.clearTimeout(t); };
  }, [idleMs, warnBeforeMs]);
  return warn;
}
